import { useMutation } from "react-query";
import {Modal, ModalBody, ModalHeader, Spinner} from "reactstrap";
import { createSegment2DMotionVideo } from "../../../_metronic/helpers/backend_helper";
import { AnnotationSegmentVideoResponse, Create2DMotionSegmentVideoRequest } from "../../../AUTO_GENERATED_TYPES";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { createPortal } from "react-dom";

interface VideoModalProps {
    show: boolean;
    onClose: () => void;
    annotationId: number;
    segmentId: string;
    creationTime: string;
    scaleFactor: number;
    appToken: string;
    screenWidth: number;
    screenHeight: number;
}

const VideoModal: React.FC<VideoModalProps> = ({show, onClose, annotationId, segmentId, creationTime, scaleFactor, appToken, screenWidth, screenHeight}) => {
    
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    

    const exitPictureInPicture = async () => {
        // @ts-ignore
        if (document.pictureInPictureElement) {
            try {
                // @ts-ignore
                await document.exitPictureInPicture();
            } catch (e) {
                // Ignore errors
            }
        }
    };

    const closeVideoModal = async () => {
        await exitPictureInPicture();
        setVideoUrl(null);
        setIsGeneratingVideo(false);
        onClose();
    };

    const { mutate: generateVideoMutation } = useMutation({
        mutationFn: ({annotationId, segmentId, creationTime, scaleFactor, appToken, screenWidth, screenHeight}: {annotationId: number, segmentId: string, creationTime: string, scaleFactor: number, appToken: string, screenWidth: number, screenHeight: number}) => {
            setIsGeneratingVideo(true);
            const requestBody: Create2DMotionSegmentVideoRequest = {
                creation_time: creationTime,
                device_info: {
                    screen_width: screenWidth,
                    screen_height: screenHeight
                },
                scale_factor: scaleFactor,
                app_token: appToken
            };
            return createSegment2DMotionVideo(annotationId, segmentId, requestBody);
        },
        onSuccess: (res: AnnotationSegmentVideoResponse) => {
            if (!res.video_url) {
                setIsGeneratingVideo(false);
                toast.error('Failed to generate video: No URL returned');
                return;
            }
            setVideoUrl(res.video_url);
            setIsGeneratingVideo(false);
        },
        onError: (err) => {
            setIsGeneratingVideo(false);
            toast.error('Failed to generate video. Please try again.');
            console.error('Video generation error:', err);
        }
    });

    useEffect(() => {
        if (show && segmentId) {
            generateVideoMutation({annotationId, segmentId, creationTime, scaleFactor, appToken, screenWidth, screenHeight});
        } else if (!show) {
            // Modal is being closed, ensure PiP is exited
            exitPictureInPicture();
        }
    }, [show, annotationId, segmentId, creationTime, scaleFactor, screenWidth, screenHeight]);

    // Change z-index of the first parent div when modal is shown, this is to ensure the modal is on top of the other modals
    // Currently a knon issue is under communtiy jobs where the image is shown on top of video modal
    useEffect(() => {
        if (show) {
            // Use setTimeout to ensure the modal DOM is rendered
            const timer = setTimeout(() => {
                // Find the first parent div with z-index: 1050 (Reactstrap modal container)
                const modalContainers = document.querySelectorAll('div[style*="z-index: 1050"]');
                if (modalContainers.length > 0) {
                    // Get the last one (most recently created) and change its z-index
                    const lastContainer = modalContainers[modalContainers.length - 1] as HTMLElement;
                    lastContainer.style.zIndex = '9999';
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [show]);

    if (!show) return null;

    const modalContent = (
        <Modal isOpen={show}
        toggle={closeVideoModal}
        centered
        size="lg"
        style={{zIndex: 9999}}>
     <ModalHeader toggle={closeVideoModal}>
         {isGeneratingVideo ? 'Generating Video' : 'Segment Video'}
     </ModalHeader>
     <ModalBody>
         {isGeneratingVideo ? (
             <div className="d-flex flex-column align-items-center justify-content-center p-5">
                 <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                 <div className="mt-3">Generating video...</div>
             </div>
         ) : videoUrl ? (
             <video 
                 ref={videoRef}
                 controls 
                 style={{ width: '100%', maxHeight: '70vh' }}
                 src={videoUrl}
                 autoPlay
             >
                 Your browser does not support the video tag.
             </video>
         ) : (
             <div className="text-center text-danger p-5">
                 Failed to load video. Please try again.
             </div>
         )}
     </ModalBody>
        </Modal>
    );

    // Use portal to render modal outside the current component tree
    return createPortal(modalContent, document.body);
}

export default VideoModal

